import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CODIGOS_ENLACE_CERRADO, SesionEnlace, SesionEnlaceService } from '../../services/sesion-enlace.service';

type Estado = 'cargando' | 'otp' | 'bloqueado' | 'cerrado' | 'error';

interface Mensaje {
  titulo: string;
  detalle: string;
}

const MENSAJES_CIERRE: Record<string, Mensaje> = {
  LINK_INVALID: {
    titulo: 'Enlace vencido',
    detalle: 'El enlace de acceso a este formulario ya no está vigente. Si necesita diligenciarlo, contacte al administrador del proceso.'
  },
  LINK_EXPIRED: {
    titulo: 'Enlace vencido',
    detalle: 'El plazo para diligenciar este formulario terminó. Si necesita diligenciarlo, contacte al administrador del proceso.'
  },
  LINK_TASK_NOT_AVAILABLE: {
    titulo: 'Formulario no disponible',
    detalle: 'La tarea asociada a este enlace ya no está disponible. Si considera que esto es un error, contacte al administrador del proceso.'
  },
  TASK_ALREADY_COMPLETED: {
    titulo: 'Formulario ya diligenciado',
    detalle: 'Este formulario ya fue completado anteriormente. No es posible diligenciarlo nuevamente.'
  }
};

/**
 * Verificación OTP del enlace de formulario expuesto. Se usa como pantalla (al abrir el enlace)
 * y como modal (cuando la sesión vence con el formulario abierto).
 */
@Component({
  selector: 'app-otp-enlace',
  templateUrl: './otp-enlace.component.html',
  styleUrl: './otp-enlace.component.css'
})
export class OtpEnlaceComponent implements OnInit, OnDestroy {
  @Input() clave: string | null = null;
  /** 'modal': se muestra sobre el formulario y permite cancelar. */
  @Input() modo: 'pagina' | 'modal' = 'pagina';
  @Output() validado = new EventEmitter<SesionEnlace>();
  @Output() cancelado = new EventEmitter<void>();

  estado: Estado = 'cargando';
  otp = '';
  destinatarios: string[] = [];
  mensajeError = '';
  cierre: Mensaje = MENSAJES_CIERRE['LINK_INVALID'];
  bloqueadoHasta: Date | null = null;
  segundosParaReenvio = 0;
  enviando = false;
  validando = false;

  private temporizador?: ReturnType<typeof setInterval>;
  private dejarDeEsperarPresencia?: () => void;

  constructor(private sesionEnlace: SesionEnlaceService) {}

  async ngOnInit() {
    if (!this.clave) {
      this.cerrar('LINK_INVALID');
      return;
    }

    try {
      const respuesta = await this.sesionEnlace.validar(this.clave);
      if (!respuesta.Ok) {
        this.aplicarError(respuesta);
        return;
      }

      const datos = respuesta.Data;
      if (datos.BloqueadoHasta) {
        this.bloquear(datos.BloqueadoHasta);
        return;
      }

      if (datos.OtpVigente) {
        // Se recargó la página con un código ya enviado: no se envía otro
        this.estado = 'otp';
        this.destinatarios = datos.Destinatarios ?? [];
        this.iniciarCuentaRegresiva(datos.SegundosParaReenvio ?? 0);
      } else {
        // Los filtros de correo (p. ej. Safe Links) abren el enlace antes de entregar la notificación:
        // el primer código sale solo cuando una persona tiene la página a la vista
        await this.esperarPresencia();
        await this.enviarOtp();
      }
    } catch (error: any) {
      this.aplicarErrorHttp(error);
    }
  }

  ngOnDestroy() {
    this.detenerCuentaRegresiva();
    this.dejarDeEsperarPresencia?.();
  }

  /**
   * Resuelve cuando la página está visible y con foco en un navegador no automatizado,
   * o cuando la persona interactúa con ella. Quien abre el enlace desde el correo cumple
   * la condición de inmediato; un escáner que la carga en segundo plano no.
   */
  private esperarPresencia(): Promise<void> {
    const hayPersona = () => !navigator.webdriver && document.visibilityState === 'visible' && document.hasFocus();
    if (hayPersona()) {
      return Promise.resolve();
    }

    const eventos = ['focus', 'visibilitychange', 'pointerdown', 'keydown'];
    return new Promise(resolve => {
      const revisar = (evento: Event) => {
        const interaccion = evento.type === 'pointerdown' || evento.type === 'keydown';
        if (hayPersona() || (interaccion && !navigator.webdriver)) {
          this.dejarDeEsperarPresencia?.();
          resolve();
        }
      };
      eventos.forEach(e => window.addEventListener(e, revisar, true));
      this.dejarDeEsperarPresencia = () => {
        eventos.forEach(e => window.removeEventListener(e, revisar, true));
        this.dejarDeEsperarPresencia = undefined;
      };
    });
  }

  async enviarOtp() {
    if (!this.clave || this.enviando || this.segundosParaReenvio > 0) {
      return;
    }

    this.enviando = true;
    this.mensajeError = '';
    try {
      const respuesta = await this.sesionEnlace.enviarOtp(this.clave);
      if (respuesta.Ok) {
        this.estado = 'otp';
        this.destinatarios = respuesta.Data?.Destinatarios ?? [];
        this.otp = '';
        this.iniciarCuentaRegresiva(respuesta.Data?.SegundosParaReenvio ?? 0);
      } else if (respuesta.CodeError === 'OTP_ESPERA') {
        this.estado = 'otp';
        this.iniciarCuentaRegresiva(respuesta.Data?.SegundosParaReenvio ?? 0);
      } else {
        this.aplicarError(respuesta);
      }
    } catch (error: any) {
      this.aplicarErrorHttp(error);
    } finally {
      this.enviando = false;
    }
  }

  async validarOtp() {
    const codigo = this.otp.trim();
    if (!this.clave || this.validando) {
      return;
    }
    if (!codigo) {
      this.mensajeError = 'Ingrese el código de verificación.';
      return;
    }

    this.validando = true;
    this.mensajeError = '';
    try {
      const respuesta = await this.sesionEnlace.validarOtp(this.clave, codigo);
      if (respuesta.Ok) {
        this.sesionEnlace.aplicarSesion(respuesta.Data);
        this.validado.emit(respuesta.Data);
      } else if (respuesta.CodeError === 'OTP_INVALIDO') {
        const restantes = respuesta.Data?.IntentosRestantes;
        this.mensajeError = restantes != null
          ? `El código no es válido o ya venció. Le quedan ${restantes} ${restantes === 1 ? 'intento' : 'intentos'}.`
          : 'El código no es válido o ya venció.';
        this.otp = '';
      } else {
        this.aplicarError(respuesta);
      }
    } catch (error: any) {
      this.aplicarErrorHttp(error);
    } finally {
      this.validando = false;
    }
  }

  cancelar() {
    this.cancelado.emit();
  }

  /** Tras un bloqueo o un error de comunicación, vuelve a consultar el estado del enlace. */
  reintentar() {
    this.estado = 'cargando';
    this.mensajeError = '';
    this.ngOnInit();
  }

  private aplicarError(respuesta: any) {
    const codigo: string = respuesta?.CodeError ?? '';
    if (CODIGOS_ENLACE_CERRADO.includes(codigo)) {
      this.cerrar(codigo);
    } else if (codigo === 'OTP_BLOQUEADO') {
      this.bloquear(respuesta.Data?.BloqueadoHasta);
    } else {
      this.estado = this.estado === 'cargando' ? 'error' : this.estado;
      this.mensajeError = respuesta?.Error || 'No fue posible completar la verificación. Intente nuevamente.';
    }
  }

  private aplicarErrorHttp(error: any) {
    this.estado = this.estado === 'cargando' ? 'error' : this.estado;
    this.mensajeError = error?.status === 429
      ? 'Demasiadas solicitudes. Espere un minuto e intente nuevamente.'
      : 'No fue posible comunicarse con el servidor. Intente nuevamente.';
  }

  private cerrar(codigo: string) {
    this.detenerCuentaRegresiva();
    this.cierre = MENSAJES_CIERRE[codigo] ?? MENSAJES_CIERRE['LINK_INVALID'];
    this.estado = 'cerrado';
  }

  private bloquear(hasta: string | null | undefined) {
    this.detenerCuentaRegresiva();
    this.bloqueadoHasta = hasta ? new Date(hasta) : null;
    this.estado = 'bloqueado';
  }

  private iniciarCuentaRegresiva(segundos: number) {
    this.detenerCuentaRegresiva();
    this.segundosParaReenvio = Math.max(0, Math.ceil(segundos));
    if (this.segundosParaReenvio === 0) {
      return;
    }
    this.temporizador = setInterval(() => {
      this.segundosParaReenvio--;
      if (this.segundosParaReenvio <= 0) {
        this.detenerCuentaRegresiva();
      }
    }, 1000);
  }

  private detenerCuentaRegresiva() {
    if (this.temporizador) {
      clearInterval(this.temporizador);
      this.temporizador = undefined;
    }
    this.segundosParaReenvio = Math.max(0, this.segundosParaReenvio);
  }
}
